"use client";

import { Eye } from "lucide-react";
import { useTranslations } from "next-intl";
import type * as React from "react";
import { useState } from "react";
import { toast } from "sonner";
import { useComparingProductsStore } from "@/features/products/ui/store";
import { useSiteSettings } from "@/features/settings/ui/hooks/useSiteSettings";
import { VirtualTryOnTrigger } from "@/features/virtual-try-on/ui";
import { Link } from "@/i18n/navigation";
import { AnalyticsEvents, trackEvent } from "@/lib/client/analytics/product-analytics";
import { Image } from "@/lib/client/components/Image";
import { useShoppingCartStore } from "@/lib/client/stores";
import { useCountryStore } from "@/lib/client/stores/useCountryStore";
import { placeholderPaths } from "@/lib/shared/constants";
import type { ProductForClient, ProductVariantForClient } from "@/lib/shared/types/interfaces";
import { LabelsEnum } from "@/lib/shared/types/LabelsEnum";
import { calculatedPercentageOff } from "@/lib/shared/utils/mathHelper";
import { translateProductVariantLabel } from "@/lib/shared/utils/productVariantLabel";
import { AspectRatio, Box, Button, Checkbox, cn, HStack, Label, Text, VStack } from "@/ui";
import { QuickViewModal } from "./QuickViewModal";

interface ProductCardProps {
  product: ProductForClient;
  /** When set, avoids a client settings fetch that can shift card aspect ratios. */
  imageRatio?: number;
  className?: string;
  onProductClick?: () => void;
}

export const ProductCard = (props: ProductCardProps) => {
  const { product, imageRatio: imageRatioProp, className, onProductClick } = props;
  const [hoveredProduct, setHoveredProduct] = useState<ProductForClient>();
  const { addItem, setIsOpen } = useShoppingCartStore();
  const { country } = useCountryStore();
  const { addProduct, removeProduct, isCompared, canAddMore } = useComparingProductsStore();
  const { imageRatio: imageRatioFromSettings } = useSiteSettings();
  const imageRatio = imageRatioProp ?? imageRatioFromSettings;
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const generalT = useTranslations("general");
  const productDetailsT = useTranslations("product_details");
  const [selectedVariant, setSelectedVariant] = useState<ProductVariantForClient>(() => {
    const v = product.variants[0];
    if (!v) {
      throw new Error("ProductCard requires at least one variant");
    }
    return v;
  });
  const productId = String(product.id);
  const compared = isCompared(productId);

  const firstImage = product.images?.[0];
  const secondImage = product.images?.[1];
  const hasSecondImage = Boolean(secondImage);
  const primaryGridSrc = (firstImage?.src || "").trim() || placeholderPaths.image;
  const secondaryGridSrc = secondImage
    ? (secondImage.src || "").trim() || placeholderPaths.image
    : undefined;

  const onMouseOver = () => {
    setHoveredProduct(product);
    trackEvent(AnalyticsEvents.PRODUCT_IMAGE_HOVERED, {
      product_id: product.id,
      sku: product.sku,
      has_second_image: hasSecondImage,
    });
  };

  const onMouseLeave = () => {
    setHoveredProduct(undefined);
  };

  const handleProductClick = () => {
    trackEvent(AnalyticsEvents.PRODUCT_CARD_CLICKED, {
      product_id: product.id,
      sku: product.sku,
      name: product.name,
    });
    onProductClick?.();
  };

  const handleCompareToggle = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    if (!compared) {
      if (!canAddMore()) {
        trackEvent(AnalyticsEvents.PRODUCT_COMPARE_LIMIT_REACHED, {
          attempted_count: 4,
        });
        toast.info(generalT("compare_limit_reached"));
        return;
      }
      trackEvent(AnalyticsEvents.PRODUCT_COMPARE_ADDED, {
        product_id: product.id,
        sku: product.sku,
        compare_count: 1,
      });
      addProduct(product);
      return;
    }
    trackEvent(AnalyticsEvents.PRODUCT_COMPARE_REMOVED, {
      product_id: product.id,
      sku: product.sku,
      compare_count: 0,
    });
    removeProduct(product);
  };

  const handleAddToCart = () => {
    trackEvent(AnalyticsEvents.PRODUCT_ADDED_TO_CART, {
      product_id: product.id,
      product_name: product.name,
      sku: product.sku,
      variant_id: selectedVariant.variant.id,
      quantity: 1,
      price: selectedVariant.onSalePrice || selectedVariant.price,
      currency: country.currencyCode,
    });
    addItem(product, selectedVariant.variant);
    setIsOpen(true);
  };

  return (
    <>
      <VStack
        key={product.id}
        className={cn(className)}
        data-product-sku={product.sku}
        data-testid="product-card"
      >
        <VStack className="relative min-h-0 flex-1 flex flex-col">
          <div
            className="group relative"
            data-testid="product-card-media"
          >
            <AspectRatio
              className={"overflow-hidden hover:cursor-pointer"}
              ratio={imageRatio}
              onMouseEnter={() => onMouseOver()}
              onMouseLeave={() => onMouseLeave()}
            >
              <Link
                href={`/products/${product.sku}`}
                className="block size-full"
                onClick={(event) => {
                  if (event.defaultPrevented) return;
                  handleProductClick();
                }}
              >
                <Image
                  src={primaryGridSrc}
                  alt={firstImage?.alt || product.name}
                  fill
                  quality={70}
                  loading="lazy"
                  sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                  decoding="async"
                  style={{
                    objectFit: "cover",
                  }}
                  className={cn(
                    hasSecondImage &&
                      "z-1 transition-all duration-700 ease-out group-hover:opacity-0 group-hover:scale-105",
                    "cursor-pointer object-center",
                  )}
                />
                {secondImage ? (
                  <Image
                    src={secondaryGridSrc ?? placeholderPaths.image}
                    alt={secondImage.alt || product.name}
                    fill
                    quality={60}
                    loading="lazy"
                    sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                    decoding="async"
                    style={{
                      objectFit: "cover",
                    }}
                    className={cn(
                      "z-2 opacity-0 transition-all duration-700 ease-out group-hover:opacity-100",
                      "cursor-pointer object-center",
                    )}
                  />
                ) : null}
              </Link>
              {/* Luxury Quick View button - revealed on hover with elegant transition */}
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                className={cn(
                  "absolute right-4 top-4 z-20 rounded-none border-0 bg-background/95 text-foreground shadow-sm transition-all duration-500 ease-out hover:bg-primary hover:text-primary-foreground hover:shadow-gold",
                  "opacity-100 md:pointer-events-none md:translate-y-2 md:opacity-0 md:group-hover:pointer-events-auto md:group-hover:translate-y-0 md:group-hover:opacity-100",
                )}
                aria-label={generalT("quick_view")}
                data-testid="product-card-quick-view-trigger"
                onClick={() => setQuickViewOpen(true)}
                title={generalT("quick_view")}
              >
                <Eye className="h-4 w-4" />
              </Button>
              <HStack
                className={cn(
                  hoveredProduct?.id === product.id
                    ? "animate-in slide-in-from-bottom"
                    : "md:hidden",
                  "duration-600 absolute bottom-0 z-20 mt-4 flex w-full cursor-pointer items-center justify-center gap-2 bg-base-300 py-2 text-center",
                )}
                onClick={handleCompareToggle}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  handleCompareToggle(event);
                }}
                role="button"
                tabIndex={0}
              >
                <Checkbox
                  id={product.id.toString()}
                  checked={compared}
                  className={
                    "pointer-events-none h-5 w-5 rounded-md border-2 border-base-content bg-base-100 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  }
                />
                <Label
                  htmlFor={product.id.toString()}
                  className="cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:text-foreground/45"
                >
                  {generalT("add_to_compare")}
                </Label>
              </HStack>
            </AspectRatio>
            {/* Luxury badges - minimal, elegant */}
            <HStack className="absolute left-3 top-3 z-10 gap-2">
              {product.labels?.includes(LabelsEnum.new) && (
                <span className="px-3 py-1 text-[10px] font-medium uppercase tracking-[0.15em] bg-primary text-primary-foreground">
                  {generalT("new_badge")}
                </span>
              )}
              {selectedVariant.onSalePrice && (
                <span className="px-3 py-1 text-[10px] font-medium uppercase tracking-[0.15em] bg-destructive text-white">
                  -{calculatedPercentageOff(selectedVariant.price, selectedVariant.onSalePrice)}%
                </span>
              )}
            </HStack>
          </div>
          {/* Luxury product info - refined typography */}
          <VStack className="relative mt-5 flex-1 justify-between gap-3">
            <VStack className="gap-1">
              {/* Category label */}
              <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                {productDetailsT("default_category")}
              </span>
              {/* Product name - serif elegance */}
              <Link
                href={`/products/${product.sku}`}
                className={"group/title cursor-pointer"}
                onClick={(event) => {
                  if (event.defaultPrevented) return;
                  onProductClick?.();
                }}
              >
                <Text className="font-product-title text-base font-medium text-foreground transition-colors duration-300 group-hover/title:text-primary">
                  {product.name}
                </Text>
              </Link>
              {/* Variant selector - minimal */}
              {product.variants.length > 1 && (
                <HStack className={"mt-2 gap-2"}>
                  {product.variants.map((variant) => (
                    <button
                      key={variant.variant.id}
                      className={cn(
                        "text-[11px] font-normal transition-all duration-300",
                        variant.variant.id === selectedVariant.variant.id
                          ? "text-foreground border-b border-primary pb-px"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                      onClick={() => setSelectedVariant(variant)}
                    >
                      {translateProductVariantLabel(productDetailsT, variant.variant.variant)}
                    </button>
                  ))}
                </HStack>
              )}
            </VStack>

            {/* Price - elegant layout */}
            <Box className="pt-2 text-right">
              {selectedVariant.onSalePrice ? (
                <HStack className="items-baseline justify-end gap-2">
                  <span className="font-price text-sm text-muted-foreground line-through">
                    {country.symbol}
                    {Number(selectedVariant.price * country.exchangeRate).toFixed(2)}
                  </span>
                  <span className="font-price font-medium text-foreground">
                    {country.symbol}
                    {Number(selectedVariant.onSalePrice * country.exchangeRate).toFixed(2)}
                  </span>
                </HStack>
              ) : (
                <span className="font-price font-medium text-foreground">
                  {country.symbol}
                  {Number(selectedVariant.price * country.exchangeRate).toFixed(2)}
                </span>
              )}
            </Box>
          </VStack>
        </VStack>

        <VirtualTryOnTrigger
          product={product}
          initialVariantId={selectedVariant.variant.id}
          fullWidth
          className="mt-4"
        />
        {/* Luxury Add to Cart - solid, elegant */}
        <Button
          onClick={handleAddToCart}
          className="mt-3 w-full bg-foreground text-background rounded-none py-5 text-[11px] font-medium uppercase tracking-[0.15em] hover:bg-primary hover:text-primary-foreground hover:shadow-gold transition-all duration-500"
        >
          {generalT("add_to_cart")}
        </Button>
      </VStack>
      <QuickViewModal
        product={product}
        open={quickViewOpen}
        onOpenChange={setQuickViewOpen}
      />
    </>
  );
};
